<script setup lang="ts">
import { startOfDay, subDays } from 'date-fns'
import Button from 'primevue/button'
import DatePicker from 'primevue/datepicker'
import Divider from 'primevue/divider'
import { computed, ref, useAttrs } from 'vue'

export type DateRange = {
  start?: Date
  end?: Date
}

const props = defineProps<{
  modelValue: DateRange | undefined
}>()

const attrs = useAttrs()

const emit = defineEmits<{
  (e: 'update:modelValue', value: DateRange | undefined): void
}>()

const now = ref(new Date())
const ranges = computed(() => {
  const end = now.value
  return {
    Today: { start: startOfDay(now.value), end },
    'Last 7 Days': { start: startOfDay(subDays(now.value, 7)), end },
    'Last 30 Days': { start: startOfDay(subDays(now.value, 30)), end }
  }
})

function selectRange(range: DateRange | undefined) {
  emit('update:modelValue', range)
}

function show() {
  now.value = new Date()
}

const model = computed({
  get: () => {
    return (
      props.modelValue?.start && props.modelValue?.end ? [props.modelValue.start, props.modelValue.end] : []
    ) as Date[]
  },
  set: val => {
    if (val[0] && val[1]) {
      emit('update:modelValue', { start: val[0], end: val[1] })
    }
  }
})
</script>

<template>
  <DatePicker
    v-bind="attrs"
    :manual-input="false"
    :hideOnRangeSelection="true"
    v-model="model"
    selectionMode="range"
    :update-model-type="'date'"
    @show="show"
  >
    <template #footer>
      <Divider />
      <div class="flex flex-col gap-3">
        <div class="flex justify-between">
          <Button v-for="(range, key) in ranges" :key="key" size="small" @click="selectRange(range)" variant="outlined">
            {{ key }}
          </Button>
        </div>
        <div class="flex justify-center">
          <Button data-testId="clear" @click="selectRange(undefined)" size="small" class="w-25">Clear</Button>
        </div>
      </div>
    </template>
  </DatePicker>
</template>
